document.addEventListener('alpine:init', () => {
  Alpine.data('assessmentPage', (config) => ({
    state: {
      assessment: {
        id: config.assessment_id,
        grading_type: config.grading_type || 'by_question',
        details: null,
      },
      students: {
        list: [],
        loading: true,
        selectAll: false,
      },
      selectedStudent: {
        id: null,
        loading: false,
        ui: {
          error: null,
          success: null,
        },
      },
      showGradingModal: false,
      selectedResponse: {
        id: null,
        score: 0,
        max_points: 0,
        feedback: '',
        student_name: '',
        node_id: null,
        question: '',
        node_type: '',
        is_multiple_choice: false,
        is_graded: false,
        up_node_response: null,
        down_node_response: null,
        mc_choices: [],
        teacher_comments: '',
      },
      questionModal: false,
      selectedQuestion: 0,
      showEditor: false,
    },
    csrf_token: config.csrf_token,
    can_grade: config.can_grade,
    is_admin: config.is_admin,
    section_slug: config.section_slug,
    init() {
      this.fetchStudentList()
    },

    fetchStudentList() {
      this.state.students.loading = true
      this.state.selectedStudent.ui.error = null

      fetch(
        `/api/assessments/${this.state.assessment.id}/students/?is_admin=${this.is_admin}&section_slug=${this.section_slug}`
      )
        .then((response) => {
          if (response.status === 403) {
            this.state.selectedStudent.ui.error =
              'You do not have permission to view this assessment.'
            this.state.students.loading = false
            throw new Error('Permission denied')
          }
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          this.state.students.list = (data.students || []).map((student) => ({
            ...student,
            selected: false,
            loaded: true,
            responses: (student.responses || []).map((response) => ({
              ...response,
              student_id: student.id,
            })),
            feedback: student.feedback || '',
          }))

          this.state.students.loading = false
        })
        .catch((error) => {
          console.error('Error fetching student list:', error)
          this.state.students.loading = false
          if (!this.state.selectedStudent.ui.error) {
            this.state.selectedStudent.ui.error =
              'Failed to load student list. Please try again.'
          }
        })
    },
    postFeedbackToAPI(responseId, feedback, score) {
      return fetch(`/api/assessment-responses/${responseId}/grade/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.csrf_token,
        },
        body: JSON.stringify({ feedback, score, is_saved: true }),
      })
        .then((response) => {
          if (response.status === 403) {
            this.state.selectedStudent.ui.error =
              'You do not have permission to grade this assessment.'
            throw new Error('Permission denied')
          }
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          // Update the student in the state with the fresh data from the API
          if (data.student) {
            this.updateStudentInState(data.student)

            // Update the selected response if it's currently open
            if (
              this.state.selectedResponse &&
              this.state.selectedResponse.id === responseId
            ) {
              const updatedResponse =
                data.student.responses &&
                data.student.responses.find((r) => r.id === responseId)
              if (updatedResponse) {
                this.state.selectedResponse = {
                  ...this.state.selectedResponse,
                  ...updatedResponse,
                  student_name: data.student.name,
                }
              }
            }
          }
          return data
        })
        .catch((error) => {
          console.error('Error updating feedback:', error)
          this.showError(error.message || 'Failed to save feedback')
          throw error // Re-throw to be caught by the caller
        })
    },
    get sortedStudents() {
      return [...this.state.students.list].sort((a, b) => {
        if (a.is_graded !== b.is_graded) {
          return a.is_graded ? 1 : -1
        }
        if (a.is_submitted !== b.is_submitted) {
          return b.is_submitted ? 1 : -1
        }
        return 0
      })
    },

    responseIsMultipleChoice(response) {
      if (response.mc_choices.length > 0) {
        return true
      }
      return false
    },

    getShortGrade(response) {
      const maxPoints = response.max_points || 0

      if (!response.is_graded) {
        return `... / ${maxPoints}`
      }

      const score = response.score || 0
      return `${score} / ${maxPoints}`
    },

    choiceIsSelected(choice) {
      return this.state.selectedResponse.selected_choices.includes(choice.id)
    },

    openGradingModal(response) {
      this.state.selectedResponse = {
        id: response.id,
        score: response.score || 0,
        max_points: response.max_points || 0,
        feedback: response.feedback || '',
        student_name: response.student_name || '',
        node_id: response.node_id || null,
        question: response.question || '',
        question_number: response.question_number || '',
        node_type: response.node_type || '',
        is_multiple_choice: response.is_multiple_choice || false,
        is_graded: response.is_graded || false,
        student_id: response.student_id || null,
        mc_choices: response.mc_choices || [],
        teacher_comments: response.teacher_comments || '',
        content: response.content || '',
        student_name: response.student_name || '',
        selected_choices: response.selected_choices || [],
        loading: false,
      }
      this.state.showGradingModal = true
      this.state.selectedStudent.id = response.student_id

      // Set up keyboard navigation after modal is fully rendered
      this.$nextTick(() => {
        setTimeout(() => {
          this.setupKeyboardNavigation()
        }, 100)
      })
    },

    getUpNodeResponse() {
      if (
        !this.state.selectedResponse.student_id ||
        !this.state.selectedResponse.node_id
      ) {
        return null
      }

      const currentStudentIndex = this.state.students.list.findIndex(
        (s) => s.id === this.state.selectedResponse.student_id
      )
      if (currentStudentIndex <= 0) {
        return null
      }

      for (let i = currentStudentIndex - 1; i >= 0; i--) {
        const student = this.state.students.list[i]
        if (student.responses && student.responses.length > 0) {
          const sameQuestionResponse = student.responses.find(
            (r) => r.node_id === this.state.selectedResponse.node_id
          )
          if (sameQuestionResponse) {
            return {
              ...sameQuestionResponse,
              student_name: student.name,
            }
          }
        }
      }
      return null
    },

    getDownNodeResponse() {
      if (
        !this.state.selectedResponse.student_id ||
        !this.state.selectedResponse.node_id
      ) {
        return null
      }

      const currentStudentIndex = this.state.students.list.findIndex(
        (s) => s.id === this.state.selectedResponse.student_id
      )
      if (
        currentStudentIndex === -1 ||
        currentStudentIndex >= this.state.students.list.length - 1
      ) {
        return null
      }

      for (
        let i = currentStudentIndex + 1;
        i < this.state.students.list.length;
        i++
      ) {
        const student = this.state.students.list[i]
        if (student.responses && student.responses.length > 0) {
          const sameQuestionResponse = student.responses.find(
            (r) => r.node_id === this.state.selectedResponse.node_id
          )
          if (sameQuestionResponse) {
            return {
              ...sameQuestionResponse,
              student_name: student.name,
            }
          }
        }
      }
      return null
    },

    updateResponseScoreInStudent(responseId, updatedScore) {
      const studentIndex = this.state.students.list.findIndex(
        (s) => s.id === this.state.selectedStudent.id
      )
      if (studentIndex !== -1) {
        const responseIndex = this.state.students.list[
          studentIndex
        ].responses.findIndex((r) => r.id === responseId)
        if (responseIndex !== -1) {
          this.state.students.list[studentIndex].responses[
            responseIndex
          ].score = updatedScore
          this.state.students.list[studentIndex].responses[
            responseIndex
          ].is_graded = true
        }
      }
    },

    updateFeedback(responseId) {
      const response = this.state.selectedResponse
      if (!response) return

      const feedback = response.teacher_comments || response.feedback || ''
      const score = response.score || 0

      this.postFeedbackToAPI(responseId, feedback, score)
        .then((data) => {
          if (data.error) {
            throw new Error(data.error)
          }
          this.showSuccess('Feedback saved')
          this.updateResponseScoreInStudent(responseId, score)
        })
        .catch((error) => {
          console.error('Error updating feedback:', error)
          this.showError(error.message || 'Failed to save feedback')
        })
    },

    showSuccess(message) {
      this.state.selectedStudent.ui.error = null
      this.state.selectedStudent.ui.success = message
      setTimeout(() => {
        this.state.selectedStudent.ui.success = null
      }, 3000)
    },

    showError(message) {
      this.state.selectedStudent.ui.success = null
      this.state.selectedStudent.ui.error = message
    },

    updateStudentInState(updatedStudent) {
      if (!updatedStudent) return

      const studentIndex = this.state.students.list.findIndex(
        (s) => s.id === updatedStudent.id
      )
      if (studentIndex === -1) return

      const selected = this.state.students.list[studentIndex].selected
      const loaded = this.state.students.list[studentIndex].loaded

      this.state.students.list[studentIndex] = {
        ...updatedStudent,
        selected: selected,
        loaded: loaded,
      }

      return this.state.students.list[studentIndex]
    },

    navigateToPreviousResponse() {
      const upResponse = this.getUpNodeResponse()
      if (upResponse) {
        this.openGradingModal(upResponse)
      }
    },

    navigateToNextResponse() {
      const downResponse = this.getDownNodeResponse()
      if (downResponse) {
        this.openGradingModal(downResponse)
      }
    },

    updateResponseScore(score) {
      this.state.selectedResponse.score = score
      this.state.selectedResponse.is_graded = true
      this.updateFeedback(this.state.selectedResponse.id)
    },

    saveFeedback() {
      const response = this.state.selectedResponse
      if (!response) return

      const feedbackContent = response.teacher_comments

      fetch(`/api/assessment-responses/${response.id}/grade/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.csrf_token,
        },
        body: JSON.stringify({
          feedback: feedbackContent,
          score: response.score,
          is_saved: true,
        }),
      })
        .then((response) => {
          if (response.status === 403) {
            this.state.selectedStudent.ui.error =
              'You do not have permission to grade this assessment.'
            throw new Error('Permission denied')
          }
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          if (data.error) {
            throw new Error(data.error)
          }

          if (data.student) {
            this.updateStudentInState(data.student)
          }

          this.showSuccess('Feedback saved successfully')
          this.state.showEditor = false
        })
        .catch((error) => {
          console.error('Error saving feedback:', error)
          this.showError(error.message || 'Failed to save feedback')
        })
    },

    excuseAssessment(studentId) {
      fetch(`/api/student-assessments/${studentId}/excuse/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.csrf_token,
        },
      })
        .then((response) => {
          if (response.status === 403) {
            this.state.selectedStudent.ui.error =
              'You do not have permission to excuse this assessment.'
            throw new Error('Permission denied')
          }
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          if (data.error) {
            throw new Error(data.error)
          } else {
            const student = this.state.students.list.find(
              (s) => s.id === studentId
            )
            if (student) {
              student.is_excused = !student.is_excused
              this.showSuccess(
                student.is_excused
                  ? 'Assessment excused'
                  : 'Assessment unexcused'
              )
            }
          }
        })
        .catch((error) => {
          console.error('Error updating excuse status:', error)
          if (!this.state.selectedStudent.ui.error) {
            this.state.selectedStudent.ui.error =
              'Failed to update excuse status'
          }
        })
    },

    retakeAssessment(studentId) {
      fetch(`/api/student-assessments/${studentId}/retake/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.csrf_token,
        },
      })
        .then((response) => {
          if (response.status === 403) {
            this.state.selectedStudent.ui.error =
              'You do not have permission to grade this assessment.'
            throw new Error('Permission denied')
          }
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          if (data.error) {
            throw new Error(data.error)
          } else {
            const student = this.state.students.list.find(
              (s) => s.id === studentId
            )
            if (student) {
              student.is_retake = !student.is_retake
              this.showSuccess('Assessment reassigned')
            }
          }
        })
        .catch((error) => {
          console.error('Error returning assessment:', error)
          if (!this.state.selectedStudent.ui.error) {
            this.state.selectedStudent.ui.error = 'Failed to return assessment'
          }
        })
    },

    submitForStudent(studentId) {
      fetch(`/api/student-assessments/${studentId}/submit/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.csrf_token,
        },
      })
        .then((response) => {
          if (response.status === 403) {
            this.state.selectedStudent.ui.error =
              'You do not have permission to submit this assessment.'
            throw new Error('Permission denied')
          }
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          if (data.error) {
            throw new Error(data.error)
          } else {
            this.updateStudentInState(data.student)
            this.showSuccess('Assessment submitted for student')
          }
        })
        .catch((error) => {
          console.error('Error submitting assessment for student:', error)
          if (!this.state.selectedStudent.ui.error) {
            this.state.selectedStudent.ui.error = 'Failed to submit assessment'
          }
        })
    },

    saveOverallFeedback(studentId) {
      const student = this.state.students.list.find((s) => s.id === studentId)
      if (!student) return

      const feedback = student.feedback || ''

      fetch(`/api/student-assessments/${studentId}/grade/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.csrf_token,
        },
        body: JSON.stringify({ feedback }),
      })
        .then((response) => {
          if (response.status === 403) {
            this.state.selectedStudent.ui.error =
              'You do not have permission to grade this assessment.'
            throw new Error('Permission denied')
          }
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          if (data.error) {
            throw new Error(data.error)
          }

          if (data.student) {
            this.updateStudentInState(data.student)
          }

          this.showSuccess('Feedback saved')
        })
        .catch((error) => {
          console.error('Error saving overall feedback:', error)
          this.showError(error.message || 'Failed to save feedback')
        })
    },

    toggleSelectAll() {
      this.state.students.selectAll = !this.state.students.selectAll
      this.state.students.list.forEach((student) => {
        if (!student.is_missing && student.is_submitted) {
          student.selected = this.state.students.selectAll
        }
      })
    },
    returnIndividualAssessment(studentId) {
      const student = this.state.students.list.find((s) => s.id === studentId)
      student.selected = true

      this.returnAssessments()
    },

    returnAssessments() {
      const assessment_ids = this.state.students.list
        .filter((s) => s.selected)
        .map((s) => s.id)

      if (assessment_ids.length === 0) {
        this.showError('No assessments selected')
        return
      }

      for (const assessment_id of assessment_ids) {
        this.state.students.list.find(
          (s) => s.id === assessment_id
        ).is_graded = true
      }

      fetch(`/api/assessments/${this.state.assessment.id}/return/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.csrf_token,
        },
        body: JSON.stringify({
          assessment_ids: assessment_ids,
          returned: true,
        }),
      })
        .then((response) => {
          if (response.status === 403) {
            this.state.selectedStudent.ui.error =
              'You do not have permission to return this assessment.'
            throw new Error('Permission denied')
          }
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          if (data.error) {
            throw new Error(data.error)
          } else {
            data.assessment_ids.forEach((assessment_id) => {
              this.state.students.list.find(
                (s) => s.id === assessment_id
              ).is_graded = true
            })
            this.showSuccess('Assessments returned to students')
          }
        })
        .catch((error) => {
          console.error('Error returning assessments:', error)
          if (!this.state.selectedStudent.ui.error) {
            this.state.selectedStudent.ui.error = 'Failed to return assessments'
          }
        })
    },

    ungradeAssessment(studentId) {
      const student = this.state.students.list.find((s) => s.id === studentId)
      if (!student) return

      // Optimistically update the UI immediately
      const originalGradedState = student.is_graded
      student.is_graded = false
      student.selected = false

      this.showSuccess('Assessment unreturned')
      const assessment_ids = [studentId]

      fetch(`/api/assessments/${this.state.assessment.id}/return/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.csrf_token,
        },
        body: JSON.stringify({
          assessment_ids: assessment_ids,
          returned: false,
        }),
      })
        .then((response) => {
          if (response.status === 403) {
            throw new Error('Permission denied')
          }
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          if (data.error) {
            throw new Error(data.error)
          }
          // Success - no need to do anything, UI is already updated
        })
        .catch((error) => {
          console.error('Error unreturning assessment:', error)

          // Revert the optimistic update
          student.is_graded = originalGradedState

          this.state.selectedStudent.ui.success = null
          this.state.selectedStudent.ui.error =
            error.message === 'Permission denied'
              ? 'You do not have permission to unreturn this assessment.'
              : 'Failed to unreturn assessment'
        })
    },

    setupKeyboardNavigation() {
      // Focus the modal for keyboard navigation
      const modal = document.getElementById('gradingModal')
      if (modal) {
        modal.focus()
      }
    },

    handleKeyboardNavigation(event) {
      if (!this.state.showGradingModal) return

      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault()
          this.navigateToPreviousResponse()
          break
        case 'ArrowDown':
          event.preventDefault()
          this.navigateToNextResponse()
          break
        case 'Escape':
          event.preventDefault()
          this.state.showGradingModal = false
          break
      }
    },
  }))
})
